import { useQuery } from '@tanstack/react-query'
import type { MapLayer, DrawArea } from '@app/types/layerTypes'
import type { RegionOption } from '@app/components/Molecules/RegionFilter'
import type { VisibleAssets } from '@app/components/Molecules/MapView/RiskMapView/RiskMapView.utilities'
import type { RiskMultiHazard } from '@app/domains/layers/types/layers.types'
import { fetchAssetMultiHazardExposureStats } from '@app/domains/layers/utils/layers.utils'

interface UseMultiHazardRiskProps {
  elementLayers: MapLayer[]
  hazardLayers: MapLayer[]
  regionMasks: RegionOption[] | null
  drawAreas: DrawArea[]
  visibleAssets: VisibleAssets
  hasElementFilters: boolean
  isAuthenticated: boolean
  clientName: string | null
}

export function useMultiHazardRisk({
  elementLayers,
  hazardLayers,
  regionMasks,
  drawAreas,
  visibleAssets,
  hasElementFilters,
  isAuthenticated,
  clientName,
}: UseMultiHazardRiskProps) {
  const regions = regionMasks?.map((mask) => mask.region) ?? []

  return useQuery<RiskMultiHazard>({
    queryKey: [
      'risk',
      'multi-hazard',
      clientName,
      elementLayers.map((layer) => layer.type),
      hazardLayers.map((layer) => layer.assetTag),
      regions,
      drawAreas,
      hasElementFilters ? visibleAssets : null,
    ],

    queryFn: async ({ signal }) => {
      const abortController = new AbortController()

      const abort = () => abortController.abort()
      signal.addEventListener('abort', abort)

      try {
        return await fetchAssetMultiHazardExposureStats(
          elementLayers,
          hazardLayers,
          regions,
          drawAreas,
          abortController,
          hasElementFilters ? visibleAssets : undefined,
        )
      } finally {
        signal.removeEventListener('abort', abort)
      }
    },

    enabled: isAuthenticated && elementLayers.length > 0 && hazardLayers.length > 1,
  })
}
